import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, Phone, X } from "lucide-react";
import { useBooking } from "../context/BookingContext";
import { useLang } from "../context/LanguageContext";
import { scrollToId } from "../lib/scroll";

const LogoMark = ({ className = "h-10 w-10" }) => (
  <img src="/dv-mark.png" alt="DV Luxury Realty" className={className} />
);

const LangToggle = ({ compact = false }) => {
  const { lang, setLang } = useLang();
  return (
    <div data-testid="nav-lang-toggle" className="flex border border-engraved">
      {["en", "es"].map((l) => (
        <button
          key={l}
          data-testid={`nav-lang-${l}`}
          onClick={() => setLang(l)}
          className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
            lang === l ? "bg-navy text-paper" : "bg-transparent text-slate-500 hover:text-crimson"
          } ${compact ? "w-full" : ""}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { openBooking } = useBooking();
  const { t } = useLang();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const goSection = (id) => {
    setOpen(false);
    if (pathname !== "/") {
      navigate(`/#${id}`);
    } else {
      scrollToId(id);
    }
  };

  const links = [
    { label: t("nav.listings"), action: () => { setOpen(false); navigate("/listings"); }, testid: "nav-listings-link" },
    { label: t("nav.creditRepair"), action: () => goSection("credit-repair"), testid: "nav-credit-repair-link" },
    { label: t("nav.downPayment"), action: () => goSection("down-payment"), testid: "nav-down-payment-link" },
    { label: t("nav.team"), action: () => goSection("team"), testid: "nav-team-link" },
  ];

  return (
    <header className="fixed inset-x-0 top-0 z-40 border-b border-engraved bg-paper/85 backdrop-blur-xl">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-8">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <LogoMark />
          <span className="leading-tight">
            <span className="block font-cinzel text-sm font-bold tracking-[0.18em] text-navy sm:text-base">
              THE VALDIVIESO GROUP
            </span>
            <span className="block text-[10px] font-semibold uppercase tracking-[0.3em] text-crimson">
              DV Luxury Realty · Las Vegas
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          {links.map((l) => (
            <button
              key={l.testid}
              data-testid={l.testid}
              onClick={l.action}
              className="text-xs font-semibold uppercase tracking-[0.2em] text-ink transition-colors hover:text-crimson"
            >
              {l.label}
            </button>
          ))}
          <a
            href="tel:+17022414761"
            data-testid="nav-phone-link"
            className="flex items-center gap-2 font-mono text-xs text-navy"
          >
            <Phone size={13} /> 702·241·4761
          </a>
          <LangToggle />
          <button
            data-testid="nav-schedule-appointment-button"
            onClick={() => openBooking({ service: "qualification" })}
            className="bg-navy px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:bg-crimson"
          >
            {t("nav.schedule")}
          </button>
        </nav>

        <div className="flex items-center gap-3 lg:hidden">
          <LangToggle />
          <button
            data-testid="nav-mobile-menu-button"
            className="text-navy"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-engraved bg-paper px-6 py-6 lg:hidden">
          <div className="flex flex-col gap-5">
            {links.map((l) => (
              <button
                key={l.testid}
                data-testid={`${l.testid}-mobile`}
                onClick={l.action}
                className="text-left text-sm font-semibold uppercase tracking-[0.2em] text-ink"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-schedule-appointment-button-mobile"
              onClick={() => { setOpen(false); openBooking({ service: "qualification" }); }}
              className="bg-navy px-5 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-paper"
            >
              {t("nav.schedule")}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
